import React from "react";
import { 
  ChevronRight, 
  Sparkles, 
  Star, 
  PlayCircle, 
  Trophy, 
  Zap, 
  ShieldCheck, 
  Info,
  Smartphone,
  Gift
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { LINKS } from "@/data/site";
import { useTranslation } from "@/i18n/LanguageContext";

// 供应商数据矩阵 - 核心 SEO 关键字载体
const SLOT_PROVIDERS = [
  { 
    id: "jili", 
    name: "JILI Slot", 
    games: "200+", 
    desc: "Fortune Gems, Super Ace, Money Coming", 
    rtp: "96.5%", 
    badge: "Most Popular",
    color: "from-blue-500/20"
  },
  { 
    id: "pragmatic", 
    name: "Pragmatic Play", 
    games: "300+", 
    desc: "Gates of Olympus, Sweet Bonanza, Starlight Princess", 
    rtp: "96.8%", 
    badge: "High Volatility",
    color: "from-orange-500/20"
  },
  { 
    id: "pgsoft", 
    name: "PG Soft", 
    games: "100+", 
    desc: "Mahjong Ways, Treasures of Aztec, Fortune Tiger", 
    rtp: "96.7%", 
    badge: "Mobile Expert",
    color: "from-purple-500/20"
  },
  { 
    id: "spadegaming", 
    name: "Spadegaming", 
    games: "150+", 
    desc: "Fishing God, Brothers Kingdom, Fafafa", 
    rtp: "96.0%", 
    badge: "Asian Themed",
    color: "from-red-500/20"
  }
];

export default function SlotPage() {
  const { t } = useTranslation();

  return (
    <div className="bg-zinc-950 text-zinc-100 min-h-screen font-sans">
      {/* 1. HERO SECTION - 强化 H1 关键字 */}
      <section className="relative pt-24 pb-20 overflow-hidden border-b border-orange-500/10">
        <div className="absolute inset-0 bg-grid opacity-20" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 text-center">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-orange-500/10 ring-1 ring-orange-500/30 text-orange-200 text-xs tracking-[0.25em] uppercase">
            <Sparkles className="w-3.5 h-3.5" /> THE #1 ONLINE SLOT MALAYSIA
          </div>
          
          <h1 className="mt-8 font-display text-5xl md:text-7xl leading-tight tracking-wider">
            U2 <span className="text-orange-grad text-glow">SLOT GAMES</span>
            <span className="block text-zinc-400 text-2xl md:text-3xl mt-4 tracking-normal">
              Premium 1,000+ Online Slot Machine Games with High RTP
            </span>
          </h1>

          <p className="mt-8 max-w-3xl mx-auto text-zinc-400 text-lg leading-relaxed">
            Discover the ultimate <strong>online slot experience at U2 Casino</strong>. 
            We carry world-class slots from <strong>JILI, Pragmatic Play, and PG Soft</strong>. 
            Enjoy progressive jackpots, free spins, and certified fair play on Malaysia's most trusted gaming platform.
          </p>

          <div className="mt-10 flex flex-wrap justify-center gap-4">
            <Button asChild size="xl" className="rounded-full px-10 h-14 bg-gradient-to-b from-orange-400 to-orange-600 text-black font-bold shine glow-orange">
              <a href={LINKS.register} target="_blank">PLAY NOW 🎰</a>
            </Button>
          </div>
        </div>
      </section>

      {/* 2. PROVIDER GRID - 丰富 LSI 关键字 */}
      <section className="py-24 max-w-7xl mx-auto px-4 sm:px-6 lg:px-10">
        <SectionHeading 
          eyebrow="LICENSED PROVIDERS" 
          title="U2 Slot Game Partners" 
          sub="Every game is audited for fairness and random number generation (RNG)."
        />

        <div className="mt-16 grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          {SLOT_PROVIDERS.map((p) => (
            <div key={p.id} className={`group relative rounded-3xl border border-zinc-800 bg-zinc-900/40 p-8 overflow-hidden hover:border-orange-500/50 transition-all duration-500`}>
              <div className={`absolute -top-12 -right-12 w-32 h-32 rounded-full bg-gradient-to-br ${p.color} blur-2xl group-hover:blur-3xl transition-all`} />
              
              <div className="relative">
                <div className="text-orange-400 text-xs font-bold tracking-widest uppercase mb-2">{p.badge}</div>
                <h3 className="text-3xl font-display tracking-wide text-zinc-50">{p.name}</h3>
                <div className="mt-4 flex items-center justify-between py-2 border-y border-zinc-800/50">
                  <span className="text-zinc-500 text-xs uppercase">Games</span>
                  <span className="text-orange-300 font-mono font-bold">{p.games}</span>
                </div>
                <div className="mt-3 text-sm text-zinc-400 leading-snug min-h-[40px]">
                  <strong>Popular:</strong> {p.desc}
                </div>
                <div className="mt-4 flex items-center justify-between bg-black/40 p-3 rounded-xl">
                  <span className="text-xs text-zinc-500 font-medium">AVG RTP</span>
                  <span className="text-sm font-bold text-green-400">{p.rtp}</span>
                </div>
                <Button asChild className="mt-6 w-full rounded-xl bg-zinc-800 group-hover:bg-orange-500 text-zinc-300 group-hover:text-black font-bold transition-all">
                  <a href={LINKS.login}>BET NOW</a>
                </Button>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* 3. SEO CONTENT SECTION - 提升专业度 (E-A-T) */}
      <section className="py-24 bg-gradient-to-b from-zinc-900/50 to-zinc-950 border-y border-orange-500/10">
        <div className="max-w-5xl mx-auto px-4">
          <div className="prose prose-invert max-w-none">
            <h2 className="text-4xl font-display text-orange-grad mb-8">How U2 Slots Work — Understanding RTP and Volatility</h2>
            <div className="grid md:grid-cols-2 gap-12 text-zinc-400 leading-relaxed text-base">
              <div>
                <h4 className="text-zinc-100 flex items-center gap-2 text-xl font-bold mb-4">
                  <Zap className="text-orange-500" /> High RTP Guarantee
                </h4>
                <p>
                  <strong>RTP (Return to Player)</strong> is the percentage that a slot pays back to players over time. 
                  At U2 Casino, we prioritize games with <strong>96%+ RTP</strong>, ensuring our Malaysian players 
                  get the best possible value for every spin.
                </p>
              </div>
              <div>
                <h4 className="text-zinc-100 flex items-center gap-2 text-xl font-bold mb-4">
                  <Trophy className="text-orange-500" /> Progressive Jackpots
                </h4>
                <p>
                  Our platform connects to major global networks, meaning <strong>jackpots can reach over RM1,000,000</strong>. 
                  Whether it's the JILI Mega Jackpot or Pragmatic Play's Daily Drops & Wins, big payouts are always within reach.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 4. FAQ - 搜索意图捕获 */}
      <section className="py-24 max-w-4xl mx-auto px-4 sm:px-6 lg:px-10">
        <SectionHeading 
          eyebrow="HELP CENTER" 
          title="Slot Games FAQ" 
          sub="Everything you need to know about playing online slots at U2."
          align="left"
        />
        <Accordion type="single" collapsible className="mt-12 space-y-4">
          <FAQItem 
            value="q1" 
            q="What is the best online slot game in Malaysia?" 
            a="Currently, Fortune Gems by JILI and Gates of Olympus by Pragmatic Play are the top-rated slots on U2 due to their high multipliers and frequent bonus rounds."
          />
          <FAQItem 
            value="q2" 
            q="Can I play U2 slots on mobile?" 
            a="Yes! All our slots are 100% mobile-optimized. You can play directly on your iOS or Android browser without any app downloads required."
          />
          <FAQItem 
            value="q3" 
            q="How do I withdraw my slot winnings?" 
            a="Withdrawing is instant at U2. Simply go to the withdrawal section, enter your amount, and funds will be transferred via FPX or DuitNow within 3-15 minutes."
          />
        </Accordion>
      </section>

      {/* 5. BOTTOM CTA - 风格呼应 */}
      <section className="pb-24 px-4">
        <div className="max-w-6xl mx-auto relative rounded-[3rem] overflow-hidden border border-orange-500/30 bg-zinc-900 p-12 text-center">
          <div className="absolute -top-24 -right-24 w-72 h-72 rounded-full bg-orange-500/20 blur-3xl" />
          <h2 className="relative z-10 font-display text-4xl md:text-5xl text-zinc-50 tracking-wider">
            READY TO HIT THE <span className="text-orange-grad">JACKPOT?</span>
          </h2>
          <p className="mt-6 text-zinc-400">Join 100,000+ active players in Malaysia today.</p>
          <div className="mt-10 relative z-10 flex flex-wrap justify-center gap-4">
            <Button asChild size="lg" className="rounded-full px-12 h-14 bg-orange-500 hover:bg-orange-400 text-black font-bold shine">
              <a href={LINKS.register}>REGISTER FREE</a>
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
}

// 辅助组件保持风格统一
function SectionHeading({ eyebrow, title, sub, align = "center" }) {
  const isCenter = align === "center";
  return (
    <div className={`max-w-2xl ${isCenter ? "mx-auto text-center" : ""}`}>
      <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-orange-500/10 ring-1 ring-orange-500/30 text-orange-200 text-[10px] tracking-[0.3em] uppercase">
        <Star className="w-3 h-3" /> {eyebrow}
      </div>
      <h2 className="mt-4 font-display text-4xl sm:text-5xl tracking-wider text-zinc-50">
        {title}
      </h2>
      {sub && <p className="mt-3 text-zinc-400 text-base">{sub}</p>}
      <div className={`mt-5 h-px bg-gradient-to-r from-transparent via-orange-500/50 to-transparent ${isCenter ? "w-48 mx-auto" : "w-32"}`} />
    </div>
  );
}

function FAQItem({ value, q, a }) {
  return (
    <AccordionItem value={value} className="border-zinc-800 px-6 rounded-2xl bg-zinc-900/30">
      <AccordionTrigger className="text-left font-display tracking-wider text-lg text-zinc-100 hover:no-underline hover:text-orange-400">
        {q}
      </AccordionTrigger>
      <AccordionContent className="text-zinc-400 text-sm leading-relaxed pb-6">
        {a}
      </AccordionContent>
    </AccordionItem>
  );
}
